import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { VehicleCurrentInfo } from 'src/app/_shared/models/vehicle';
import { FleetDutyCycleFigureService } from 'src/app/_services/fleet-duty-cycle-figure.service';
import { RbUiComponentsModule } from '@inst-iot/bosch-angular-ui-components';
import { TransformXAxisPipe } from 'src/app/_shared/pipes/transform-xaxis.pipe';

@Component({
  selector: 'app-time-spent-extreme-temp-low-ef-chart',
  standalone: true,
  imports: [CommonModule,RbUiComponentsModule],
  templateUrl: './time-spent-extreme-temp-low-ef-chart.component.html',
  styleUrls: ['./time-spent-extreme-temp-low-ef-chart.component.scss']
})
export class TimeSpentExtremeTempLowEfChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() selectedVehicles: any[] = [];
  @Input() vehiclesTempInsights: any
  @Input() timeSpentExtremeTemperatureDatapoints: any;
  @Input() fleetId: string[] = [];
  currentVehicleData: any;
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  vehiclesInfo!: { [key: string]: VehicleCurrentInfo; };
  chartRange!: { range: [number, number] };

  constructor(
    private readonly fleetDutyCycleFigureService: FleetDutyCycleFigureService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;
    let fleetData: any[]

    this.fleetDutyCycleFigureService.getTimeSpentExtremeBelowTemperatureFleet(this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (datapoints) => {
          fleetData = this.plotFleetData(datapoints);
        },
        error: (error) => {
          console.error('Error fetching Time Spent in Low Temperature Fleet data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        },
        complete: () => {
          this.fetchVehicleInfoAndUpdateChart(fleetData);
          this.dataLoading = false;
        }
      }
    )
  }

  fetchVehicleInfoAndUpdateChart(fleetData: any[]): void {
    const deviceSerialNumber: string[] = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    const vehicleNameMap: { [key: string]: string } = {};

    this.selectedVehicles.forEach(vehicle => {
      vehicleNameMap[vehicle.batteryIdentifier] = vehicle.name;
    });

    const averageLines = deviceSerialNumber.map((id, index) => {
      const time = this.vehiclesTempInsights[id].timeExtremeLowTemp;
      const vehicleName = vehicleNameMap[id];

      const maxPercentAmount = Math.max(...fleetData.flatMap(plot => plot.y));

      return {
        x: [time, time],
        y: [0, maxPercentAmount],
        type: 'scatter',
        mode: 'lines',
        name: vehicleName,
        line: {
          color: this.getVehicleColor(index),
          width: 4
        }
      };
    });

    const combinedPlotData = [...fleetData, ...averageLines];
    this.updateChart(combinedPlotData);
    this.dataLoading = false;  
  }

  getVehicleColor(index: number): string {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    return colors[index % 3];
  }

  plotFleetData(datapoints: any): any[] {
    const transformXAxisPipe = new TransformXAxisPipe();
    return Object.keys(datapoints).flatMap((fleetId: string) => {
      const plotData = datapoints[fleetId];
      
      const bin = plotData.map((dataObject: any) => dataObject.binTime);
      const percentAmount = plotData.map((dataObject: any) => dataObject.percVehExtremeLowTemp);
      this.chartRange = transformXAxisPipe.transform(bin, percentAmount, 0,100);
      
      return {
        x: bin,
        y: percentAmount,
        type: 'bar',
        name: `Fleet`,
        marker: {
          color: '#8A9097'
        }
      };
    });
  }

  updateChart(data: any[]): void {
    const layout = {
      title: 'Time Spent in Low Temperature',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: 'Accumulated operation time below 0°C/32°F [h]',
        range: this.chartRange?.range
      },
      yaxis: {
        title: 'Percentage of Fleet Vehicles [%]'
      },
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };
    createPlotlyChart('time-spent-extreme-temp-low-ef-chart', data, layout);
  }
}
